// import { Theme } from '@mui/material/styles'; // Removed unused import

/**
 * TABLE STYLING STANDARDS & GUIDELINES
 * ====================================
 * 
 * IMPORTANT: All tables in this application MUST follow these consistent styling patterns
 * to maintain visual consistency and professional appearance.
 * 
 * USAGE PATTERN:
 * 1. Import all required styling functions
 * 2. Apply getTableStyles() to <Table> component
 * 3. Apply getTableContainerStyles() to <Paper> wrapper
 * 4. Apply getTableHeaderStyles(isDarkMode) to header <TableCell> components
 * 5. Apply getTableRowStyles() to ALL <TableRow> components
 * 6. Apply getTableCellStyles() to ALL <TableCell> components
 * 7. Use consistent status chip styling (no variant="outlined")
 * 
 * IMPLEMENTATION STEPS:
 * - Import all required styling functions from this file
 * - Apply getTableContainerStyles() to Paper wrapper
 * - Apply getTableHeaderStyles(isDarkMode) to header TableCell components
 * - Apply getTableRowStyles() to all TableRow components
 * - Apply getTableCellStyles() to all TableCell components
 * - Use consistent status chip styling without variant="outlined"
 */

export const getTableHeaderStyles = (isDarkMode: boolean) => ({
  fontWeight: 'bold',
  backgroundColor: isDarkMode ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.08)',
  color: isDarkMode ? 'primary.light' : 'primary.main',
});

export const getTableContainerStyles = () => ({
  width: '100%',
  overflow: 'hidden',
});

export const getTableStyles = () => ({
  // Table-specific styles can go here
});

/**
 * STATUS CHIP STANDARDS
 * =====================
 * 
 * IMPORTANT: All status chips MUST follow these consistent patterns:
 * 
 * 1. NEVER use variant="outlined" - use default variant
 * 2. Use consistent colors:
 *    - success: for active/positive states
 *    - error: for inactive/negative states  
 *    - default: for neutral states
 *    - primary: for informational states
 * 3. Always use size="small" for consistency
 * 
 * IMPLEMENTATION:
 * - Use default variant (no variant prop)
 * - Set color based on state (success/error/default/primary)
 * - Always use size="small"
 * - Example: success color for active states, error color for inactive states
 */
export const getStatusChipStyles = (isActive: boolean) => ({
  size: 'small' as const,
  variant: 'outlined' as const, // DEPRECATED: Don't use this pattern
  color: isActive ? 'success' : 'error',
  icon: isActive ? 'CheckCircle' : 'Cancel',
  label: isActive ? 'Active' : 'Expired',
});

// Helper function to format status text with proper capitalization
export const formatStatusText = (status: string | undefined | null) => {
  if (!status) return '';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

export const getTableRowStyles = () => ({
  '&:last-child td, &:last-child th': { border: 0 },
});

export const getTableCellStyles = () => ({
  padding: '12px 16px',
});

export const getTablePaginationStyles = () => ({
  borderTop: '1px solid',
  borderColor: 'divider',
});

/**
 * HEADER LAYOUT STANDARDS
 * =======================
 * 
 * IMPORTANT: All page headers MUST follow this consistent layout pattern:
 * 
 * - Title on the left
 * - Controls and actions on the right
 * - All elements properly aligned on the same line
 * - Consistent spacing and layout across all pages
 * 
 * Example structure:
 * - Use Box with display: 'flex', justifyContent: 'space-between', alignItems: 'center'
 * - Set mb: 2 and gap: 2 for consistent spacing
 * - Place Typography variant="h5" component="h1" on the left
 * - Place action controls in a Box on the right with display: 'flex', gap: 2, alignItems: 'center'
 */ 